import { gql, useQuery } from '@apollo/client';
import IconExpandList from '@apollo/icons/default/IconExpandList.svg';
import { Button, Heading } from '@apollo/orbit';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { DocumentTitle } from 'src/components/documentTitle/DocumentTitle';
import { ErrorPage } from 'src/components/errorPage/ErrorPage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly/hooks';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { CheckSidebar } from '../checkSidebar/CheckSidebar';
import { RerunBanner, RerunBannerCheckFragment } from '../RerunBanner';
import { TaskStatusBar, TaskStatusBarTaskFragment } from '../TaskStatusBar';

import { SchemaDiff } from './SchemaDiff';
import { SchemasMenu } from './SchemasMenu';

const SUPERGRAPH_SCHEMA_ID = '@!supergraph!@';

export function CheckSchemas() {
  const { currentCheckSchemasRoute } = useInternalGraphLinking();
  const { checkId, graphId, linkedSdl } = useRouteParams(
    currentCheckSchemasRoute,
  );
  const proposalsUseHasWarnings = useLDFlag(
    'tooling-proposals-use-has-warnings',
  );

  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckSchemasQuery,
    GraphQLTypes.CheckSchemasQueryVariables
  >(
    gql`
      query CheckSchemasQuery(
        $graphId: ID!
        $checkId: ID!
        $proposalsUseHasWarnings: Boolean!
      ) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            proposedSchemaHash
            proposedSubgraphs {
              name
              hash
            }
            baseSchemaHash
            baseSubgraphs {
              hash
              name
            }
            rerunOf {
              id
              ...RerunBannerCheckFragment
            }
            tasks {
              id
              ...TaskStatusBarTaskFragment
            }
            baseVariant {
              id
              hasSupergraphSchema
            }
          }
        }
      }
      ${TaskStatusBarTaskFragment}
      ${RerunBannerCheckFragment}
    `,
    {
      variables: { graphId, checkId, proposalsUseHasWarnings },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const graph = data?.service;
  const workflow = graph?.checkWorkflow;

  const [changedSubgraphs = [], unchangedSubgraphs = []] = useMemo(() => {
    if (!workflow) return [];
    const subgraphLookup: Record<
      string,
      | { baseHash: string; proposedHash: string | null }
      | { baseHash: string | null; proposedHash: string }
    > = {};
    workflow.baseSubgraphs?.forEach((baseSubgraph) => {
      subgraphLookup[baseSubgraph.name] = {
        baseHash: baseSubgraph.hash,
        proposedHash: null,
      };
    });
    workflow.proposedSubgraphs?.forEach((proposedSubgraph) => {
      getOrSetRecord(
        proposedSubgraph.name,
        { baseHash: null, proposedHash: proposedSubgraph.hash },
        subgraphLookup,
      ).proposedHash = proposedSubgraph.hash;
    });

    return _.partition(
      Object.entries(subgraphLookup),
      ([, { baseHash, proposedHash }]) => baseHash !== proposedHash,
    );
  }, [workflow]);

  const isSupergraph = !!workflow?.baseVariant?.hasSupergraphSchema;

  const [showUnchangedSubgraphs, setShowUnchangedSubgraphs] = useState(false);
  if (loading) {
    return <Loading />;
  } else if (error) {
    return <ErrorPage error={error} />;
  } else if (!workflow) {
    return <NotFound />;
  }

  const showSupergraphUI = isSupergraph || changedSubgraphs.length > 0;
  const hasSuccessfulComposition = workflow.proposedSchemaHash !== null;
  return (
    <DocumentTitle title="Schemas">
      <div className="flex h-full">
        <CheckSidebar>
          {showSupergraphUI && (
            <SchemasMenu
              showSupergraphUI={showSupergraphUI}
              hasSuccessfulComposition={hasSuccessfulComposition}
              changedSubgraphs={changedSubgraphs}
              unchangedSubgraphs={unchangedSubgraphs}
              showUnchangedSubgraphs={showUnchangedSubgraphs}
              setShowUnchangedSubgraphs={setShowUnchangedSubgraphs}
            />
          )}
        </CheckSidebar>
        <div className="w-0 flex-1 overflow-y-auto p-6">
          <RerunBanner
            graphId={graphId}
            checkId={checkId}
            rerunOfCheck={workflow.rerunOf}
            className="mb-4"
          />
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <Heading size="2xl">Tasks</Heading>
              <TaskStatusBar tasks={workflow.tasks} />
            </div>
            <div className="flex flex-col gap-4">
              {showSupergraphUI ? (
                <>
                  <Heading size="2xl">Schemas</Heading>
                  {hasSuccessfulComposition && (
                    <SchemaDiff
                      graphId={graphId}
                      linkedSdl={linkedSdl}
                      schemaName="Supergraph"
                      schemaSlug={SUPERGRAPH_SCHEMA_ID}
                      baseHash={workflow.baseSchemaHash}
                      proposedHash={workflow.proposedSchemaHash}
                    />
                  )}
                </>
              ) : (
                <SchemaDiff
                  graphId={graphId}
                  linkedSdl={linkedSdl}
                  schemaName="Schema"
                  schemaSlug={SUPERGRAPH_SCHEMA_ID}
                  baseHash={workflow.baseSchemaHash}
                  proposedHash={workflow.proposedSchemaHash}
                />
              )}

              {changedSubgraphs.map(([subgraphName, subgraphHashes]) => {
                return (
                  <SchemaDiff
                    graphId={graphId}
                    key={subgraphName}
                    linkedSdl={linkedSdl}
                    schemaName={subgraphName}
                    schemaSlug={subgraphName}
                    baseHash={subgraphHashes.baseHash}
                    proposedHash={subgraphHashes.proposedHash}
                  />
                );
              })}

              {showUnchangedSubgraphs ? (
                unchangedSubgraphs.map(([subgraphName, subgraphHashes]) => {
                  return (
                    <SchemaDiff
                      graphId={graphId}
                      key={subgraphName}
                      linkedSdl={linkedSdl}
                      schemaName={subgraphName}
                      schemaSlug={subgraphName}
                      baseHash={subgraphHashes.baseHash}
                      proposedHash={subgraphHashes.proposedHash}
                    />
                  );
                })
              ) : unchangedSubgraphs.length > 0 ? (
                <Button
                  onClick={() => setShowUnchangedSubgraphs(true)}
                  variant="secondary"
                  className="m-auto mt-4"
                  rightIcon={<IconExpandList />}
                >
                  Show unchanged subgraphs
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
}
